import * as React from 'react';
import clx from 'classnames';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Col, message, Row, Space, Typography } from 'antd';
import { getWidgetInputLayout } from 'client/components/schema/widget/helper/getWidgetInputLayout';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { ProcedureApi } from 'client/components/schema/procedures/ProcedureApi';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { Formik, FormikProps } from 'formik';
import {
  ICreateRequestDto,
  createRequestValidator
} from 'common/dto/RequestDto';
import { FormikForm } from 'client/ui/form/FormikForm';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { sortBy } from 'lodash';
import { RequestSendSuccessMessage } from '../../../../components/schema/requests/success/RequestSendSuccessMessage';
import { FormErrorsNotification } from 'client/ui/form/errors/FormErrorsNotification';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import {
  useCurrentCitizen,
  useCurrentUser,
  usePermissions
} from 'client/components/auth/AuthModule';
import { ContextualRequestBag } from 'common/schema/contextual-value/ContextualValue';
import moment from 'moment';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { ProcedureMachine } from 'common/schema/ProcedureMachine';
import { ProcedureFormMainColumn } from './shared/ProcedureFormMainColumn';
import { ProcedureFormSummaryColumn } from './shared/ProcedureFormSummaryColumn';
import { omit, isEqual } from 'lodash';
import { useCallback, useState } from 'react';
import { useRoleNetworkErrorMessage } from 'client/core/hooks/useRoleNetworkErrorMessage';
import { PageFullRow } from 'client/ui/layout/page/PageFullRow';
import { DesktopOnlyAfterContent } from './shared/DesktopOnlyAfterContent';
import { useDraftSave } from './actions/useDraftSave';
import { RequestCompileContextProvider } from 'client/components/schema/requests/compile-context/RequestCompileContextProvider';
import { ProcedureActionButtons } from './actions/ProcedureActionButtons';
import { getNetworkResponseBag } from 'client/core/network/errors/getNetworkResponseBag';
import { RequestSendBag } from 'common/dto/response-bag/RequestSendBag';
import { RequestState } from 'common/schema/request/Request';

const { Text } = Typography;

interface Params {
  id: string;
}

export interface ProcedureFormPageProps extends RouteComponentProps<Params> {}

/**
 * Pagina del Modulo della Domanda frontend
 */
export function ProcedureFormPage(props: ProcedureFormPageProps) {
  const [secondStep, setSecondStep] = useState(false);
  const layout = getWidgetInputLayout();
  const citizen = useCurrentCitizen();
  const user = useCurrentUser();

  const id = parseInt(props.match.params.id, 10);

  const [create, createResponse] = useApiMutation(RequestApi.create, {});
  const { response, error, loading, refetch } = useApiQuery(
    ProcedureApi.frontend.find,
    {
      data: {
        id: id!
      }
    }
  );

  const procedure = response?.data;

  const [draftSave] = useDraftSave({
    procedure,
    run: async values =>
      await create({
        data: {
          procedureId: procedure?.id,
          input: { ...values }
        }
      })
  });
  // const draftValidator = createDraftRequestValidator();
  // const draftOnClick = useCallback(
  //   async (formik: FormikProps<any>, id: number) => {
  //     const rawValues = formik.values;
  //     rawValues.sendRightNow = false;
  //     try {
  //       const values = await draftValidator.validate(rawValues, {
  //         abortEarly: false,
  //         context: {
  //           request: contextualRequest
  //         }
  //       });
  //       const result = await create({
  //         data: {
  //           procedureId: id,
  //           input: { ...values }
  //         }
  //       });
  //       props.history.push(`/richieste/${result.data!.id}/modulo`);
  //       message.success('Domanda salvata in bozza.');
  //     } catch (e) {
  //       // TODO Gestire. Caso di errore di validazione OPPURe errore nell'invio
  //       console.error('Errore di validazione non gestito', e);
  //     }
  //   },
  //   [props.history, create]
  // );

  const permissions = usePermissions();

  const createAlertMessage = useRoleNetworkErrorMessage();

  const requestStateResponse = useApiQuery(RequestApi.getState, {
    skip: id == null,
    data: {
      procedureId: id!
    }
  });
  const requestState = requestStateResponse.response?.data;

  useBreadcrumbItem({
    path: '/',
    title: 'Istanze'
  });
  useBreadcrumbItem({
    path: `/domande/${id}`,
    title: procedure?.title ?? '...'
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: 'Modulo'
  });

  if (error || requestStateResponse.error)
    return (
      <AlertPage
        showIcon
        type="error"
        message="Impossibile caricare la domanda"
        description={getNetworkErrorMessage(
          error ?? requestStateResponse.error
        )}
      />
    );

  if (!procedure?.isCurrentUserEnabled && !loading) {
    return (
      <AlertPage
        showIcon
        type="error"
        message="Attenzione! Per compilare questa domanda è necessario essere autenticati e abilitati."
        description={getNetworkErrorMessage(
          error ?? requestStateResponse.error
        )}
      />
    );
  }

  if (!procedure || loading || !requestState || requestStateResponse.loading)
    return <LoadingPage />;

  const validator = createRequestValidator(procedure);

  const contextualRequest = ContextualRequestBag({
    citizen: citizen,
    sentAt: moment().toDate(),
    siap: user?.siap
  });

  if (!ProcedureMachine.allows(procedure, 'citizen.send')) {
    return (
      <AlertPage
        showIcon
        type="warning"
        message="Impossibile Compilare la Domanda"
        description="Non è possibile compilare questa domanda perché non è più Attiva."
      />
    );
  }

  if (!requestState?.canSend) {
    return (
      <AlertPage
        showIcon
        type="error"
        message="Hai già inviato questa domanda"
        description={
          <Space direction="vertical" size="small">
            <Row>
              <Text>
                Questa domanda può essere presentata una sola volta per utente.
                Puoi visualizzare la domanda che hai già compilato premendo sul
                pulsante qui sotto.
              </Text>
            </Row>

            <Row>
              <Button type="ghost">
                <Link to={`/richieste/${requestState?.requestId}/dettaglio`}>
                  Visualizza la tua domanda
                </Link>
              </Button>
            </Row>
          </Space>
        }
      />
    );
  }

  return (
    <FrontLayout>
      <Formik<ICreateRequestDto>
        validateOnMount
        initialValues={{ values: {}, sendRightNow: false, captchaTokens: {} }}
        validate={schemaToValidator(validator, {
          context: { request: contextualRequest }
        })}
        onSubmit={async rawValues => {
          try {
            rawValues.sendRightNow = true;
            const values = await validator.validate(rawValues, {
              abortEarly: false,
              context: {
                request: contextualRequest
              }
            });
            const result = await create({
              data: { procedureId: procedure.id, input: { ...values } }
            });
            if (result.data.state === RequestState.Sent) {
              message.success('Domanda inviata con successo.');
            }
          } catch (e) {
            const bag = getNetworkResponseBag<RequestSendBag>(e);
            if (bag?.requestId) {
              props.history.push(`/richieste/${bag.requestId}/modulo`);
            }
            // TODO Gestire. Caso di errore di validazione OPPURe errore nell'invio
            console.error('Errore di validazione non gestito', e);
          }
        }}
      >
        {formik => (
          <RequestCompileContextProvider
            procedure={procedure}
            request={formik.values}
          >
            <PreventNavigation
              when={
                formik.dirty &&
                !isOnlyCaptchaDirty(formik.values, formik.initialValues) &&
                !formik.isSubmitting &&
                !createResponse.response
              }
            />
            <FormErrorsNotification />
            <FormikForm {...layout} disableEnterSubmit>
              {(createResponse.loading || createResponse.response) &&
              formik.values.sendRightNow ? (
                <PageFullRow>
                  <Col span={24}>
                    <RequestSendSuccessMessage
                      procedure={procedure!}
                      request={createResponse.response?.data}
                      loading={createResponse.loading}
                    />
                  </Col>
                </PageFullRow>
              ) : (
                <PageFullRow>
                  {/* Colonna Principale */}
                  <ProcedureFormMainColumn
                    procedureTitle={procedure.title}
                    beforeContent={
                      createResponse.error && (
                        <Alert
                          message="Errore nell'invio"
                          description={createAlertMessage(createResponse.error)}
                          type="error"
                          showIcon
                          closable
                          style={{ marginBottom: '24px' }}
                        />
                      )
                    }
                    afterContent={
                      <DesktopOnlyAfterContent>
                        <ProcedureActionButtons
                          draftSave={draftSave}
                          spaceProps={{
                            className: clx('desktop-only-after-content'),
                            direction: 'horizontal',
                            align: 'end',
                            size: 0,
                            style: { width: '100%' }
                          }}
                        />
                      </DesktopOnlyAfterContent>
                    }
                  />

                  {/* Colonna Sommario / Azioni */}
                  <ProcedureFormSummaryColumn procedure={procedure}>
                    {/* Pulsanti di Azione del Modulo della Domanda */}
                    <ProcedureActionButtons
                      draftSave={draftSave}
                      spaceProps={{
                        direction: 'vertical',
                        size: 16,
                        style: { width: '100%' }
                      }}
                    />
                  </ProcedureFormSummaryColumn>
                </PageFullRow>
              )}
            </FormikForm>
          </RequestCompileContextProvider>
        )}
      </Formik>
    </FrontLayout>
  );
}

export const isOnlyCaptchaDirty = (
  raw: ICreateRequestDto,
  initial: ICreateRequestDto
) => {
  const ids = Object.keys(raw.captchaTokens);
  // Elimina dai values i valori relativi ai captcha
  const base = (obj: ICreateRequestDto) => {
    const { captchaTokens, ...others } = obj;
    const result = {
      ...others,
      values: omit(others.values, ids)
    };
    return result;
  };

  const baseRaw = base(raw);
  const baseInitial = base(initial);
  return isEqual(baseRaw, baseInitial);
};
